import { Subject, type Observable } from 'rxjs';

/**
 * 视口鼠标光标样式枚举。
 *
 * 只收录当前业务实际用到的光标值，避免开放过于宽泛的字符串。
 */
export enum ViewportCursorStyle {
  /** 默认系统光标。 */
  Default = 'default',
  /** 十字准星光标，用于旋转零部件等精确定位场景。 */
  Crosshair = 'crosshair',
  /** 四方向移动光标，用于移动零部件场景。 */
  Move = 'move',
  /** 抓手光标，用于拖拽场景。 */
  Grab = 'grab',
  /** 拖动中的抓手光标。 */
  Grabbing = 'grabbing',
}

/**
 * 光标变化事件载荷。
 */
export interface ViewportCursorChangeEvent {
  /** 新的光标样式。 */
  cursor: ViewportCursorStyle;
}

/**
 * 视口光标管理器。
 *
 * 职责：
 * 1. 统一管理视口 canvas 区域的鼠标光标样式；
 * 2. 以 Observable 派发光标变化事件，供宿主 UI 层订阅响应；
 * 3. 支持优先级栈（越晚 push 越优先），确保多个并发工具不互相覆盖。
 *
 * 设计说明：
 * - 宿主只观察事件流，不直接操作 DOM，保持 cad-core 与 DOM 解耦；
 * - 工具/控制器调用 `push/pop` 而非直接 `set`，以避免退出时漏恢复光标。
 */
export class ViewportCursorManager {
  /** 当前光标样式栈，栈顶为最高优先级。 */
  private readonly stack: ViewportCursorStyle[] = [];

  /** 光标变化事件流。 */
  private readonly cursorChange$ = new Subject<ViewportCursorChangeEvent>();

  /**
   * 订阅光标变化事件。
   *
   * @returns 光标变化 Observable。
   */
  public observeCursorChange(): Observable<ViewportCursorChangeEvent> {
    return this.cursorChange$.asObservable();
  }

  /**
   * 获取当前生效的光标样式。
   *
   * @returns 当前光标样式。
   */
  public getCurrent(): ViewportCursorStyle {
    return this.stack.length > 0
      ? this.stack[this.stack.length - 1]!
      : ViewportCursorStyle.Default;
  }

  /**
   * 压入一个光标样式到栈顶（立即生效）。
   *
   * @param cursor 要启用的光标样式。
   */
  public push(cursor: ViewportCursorStyle): void {
    this.stack.push(cursor);
    this.emit();
  }

  /**
   * 移除指定光标样式（从栈中删除最后一次 push 的同值项）。
   * 适用于工具退出时恢复前一个工具的光标。
   *
   * @param cursor 要移除的光标样式。
   */
  public pop(cursor: ViewportCursorStyle): void {
    // 从栈尾向前找，移除第一个匹配项。
    for (let i = this.stack.length - 1; i >= 0; i--) {
      if (this.stack[i] === cursor) {
        this.stack.splice(i, 1);
        break;
      }
    }
    this.emit();
  }

  /**
   * 清空整个光标栈，恢复到默认光标。
   */
  public clear(): void {
    this.stack.length = 0;
    this.emit();
  }

  /**
   * 将指定光标替换为另一个光标（从栈中找到最后一次 push 的旧值并替换）。
   * 适用于工具内部模式切换（如移动↔旋转）不退出工具直接更新光标的场景。
   *
   * @param oldCursor 要被替换的旧光标样式。
   * @param newCursor 新光标样式。
   */
  public replace(oldCursor: ViewportCursorStyle, newCursor: ViewportCursorStyle): void {
    for (let i = this.stack.length - 1; i >= 0; i--) {
      if (this.stack[i] === oldCursor) {
        this.stack[i] = newCursor;
        this.emit();
        return;
      }
    }
    // 未找到旧光标则直接压入新光标。
    this.stack.push(newCursor);
    this.emit();
  }

  /**
   * 销毁管理器，完成 Subject。
   */
  public dispose(): void {
    this.stack.length = 0;
    this.cursorChange$.complete();
  }

  /**
   * 向订阅者派发当前光标状态。
   */
  private emit(): void {
    this.cursorChange$.next({ cursor: this.getCurrent() });
  }
}
